import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Plus, ArrowLeft, ExternalLink, Globe, X, Monitor, Lock, Check, ChevronRight } from 'lucide-react';
import { EmptyState } from './EmptyState';
interface BookmarkTag {
  label: string;
}
interface Bookmark {
  id: string;
  title: string;
  url: string;
  domain: string;
  tags: string[];
  lastVisited?: string;
  pinned?: boolean;
  authRequired?: boolean;
  authStatus?: 'authenticated' | 'needs-login';
}
interface AgentTask {
  description: string;
  status: 'pending' | 'auth-required' | 'working' | 'done';
}
const BOOKMARKS: Bookmark[] = [{
  id: '1',
  title: 'Q2 Budget',
  url: 'https://docs.google.com/spreadsheets/d/1a2b3c',
  domain: 'docs.google.com',
  tags: ['Finance', 'Spreadsheet'],
  lastVisited: 'this morning',
  pinned: true,
  authRequired: true,
  authStatus: 'authenticated'
}, {
  id: '2',
  title: 'Zen Bot Roadmap',
  url: 'https://linear.app/contextloops/project/zenbot',
  domain: 'linear.app',
  tags: ['Project', 'Planning'],
  lastVisited: 'yesterday',
  pinned: true,
  authRequired: true,
  authStatus: 'authenticated'
}, {
  id: '3',
  title: 'Hetzner Console',
  url: 'https://console.hetzner.cloud/projects/123',
  domain: 'console.hetzner.cloud',
  tags: ['Infrastructure'],
  lastVisited: 'Monday',
  authRequired: true,
  authStatus: 'authenticated'
}, {
  id: '4',
  title: 'Coolify Dashboard',
  url: 'https://coolify.contextloops.com',
  domain: 'coolify.contextloops.com',
  tags: ['Infrastructure', 'Deploys'],
  lastVisited: 'this morning',
  authRequired: true,
  authStatus: 'authenticated'
}, {
  id: '5',
  title: 'Nango Dashboard',
  url: 'https://zenbot-nango.contextloops.com',
  domain: 'zenbot-nango.contextloops.com',
  tags: ['Infrastructure', 'Integrations'],
  lastVisited: 'Tuesday'
}, {
  id: '6',
  title: 'Acme Contract',
  url: 'https://docs.google.com/document/d/4d5e6f',
  domain: 'docs.google.com',
  tags: ['Legal', 'Document'],
  lastVisited: 'yesterday',
  authRequired: true,
  authStatus: 'authenticated'
}, {
  id: '7',
  title: 'Stripe Dashboard',
  url: 'https://dashboard.stripe.com',
  domain: 'dashboard.stripe.com',
  tags: ['Finance', 'Payments'],
  authRequired: true,
  authStatus: 'needs-login'
}, {
  id: '8',
  title: 'PostHog Analytics',
  url: 'https://eu.posthog.com/project/1',
  domain: 'eu.posthog.com',
  tags: ['Analytics'],
  lastVisited: 'last week',
  authRequired: true,
  authStatus: 'authenticated'
}, {
  id: '9',
  title: 'GitHub - zenbot.chat',
  url: 'https://github.com/contextloops/zenbot.chat',
  domain: 'github.com',
  tags: ['Code'],
  lastVisited: 'this morning'
}, {
  id: '10',
  title: 'Figma - UI Redesign',
  url: 'https://figma.com/file/abc123',
  domain: 'figma.com',
  tags: ['Design'],
  lastVisited: 'Wednesday',
  authRequired: true,
  authStatus: 'needs-login'
}, {
  id: '11',
  title: 'TMA Patient Portal',
  url: 'https://app.tinymedicalapps.com',
  domain: 'app.tinymedicalapps.com',
  tags: ['TMA', 'Product'],
  authRequired: true,
  authStatus: 'authenticated'
}, {
  id: '12',
  title: 'Tailwind Docs',
  url: 'https://tailwindcss.com/docs',
  domain: 'tailwindcss.com',
  tags: ['Reference'],
  lastVisited: 'yesterday'
}];
const ALL_TAGS = ['Finance', 'Spreadsheet', 'Project', 'Planning', 'Infrastructure', 'Deploys', 'Integrations', 'Legal', 'Document', 'Payments', 'Analytics', 'Code', 'Design', 'TMA', 'Product', 'Reference'];
export function BrowserView() {
  const [search, setSearch] = useState('');
  const [activeTags, setActiveTags] = useState<string[]>([]);
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newUrl, setNewUrl] = useState('');
  if (viewingId) {
    const bm = BOOKMARKS.find(b => b.id === viewingId);
    if (bm) return <BrowserDetail bookmark={bm} onBack={() => setViewingId(null)} data-id="element-287" />;
  }
  const toggleTag = (tag: string) => setActiveTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  const filtered = BOOKMARKS.filter(b => {
    if (search && !b.title.toLowerCase().includes(search.toLowerCase()) && !b.url.toLowerCase().includes(search.toLowerCase())) return false;
    if (activeTags.length > 0 && !b.tags.some(t => activeTags.includes(t))) return false;
    return true;
  });
  const pinned = filtered.filter(b => b.pinned);
  const rest = filtered.filter(b => !b.pinned);
  return <div className="min-h-[calc(100vh-56px)] text-[#e8e4df] pt-12 pb-32 px-6" data-id="element-288">
      <div className="max-w-[640px] mx-auto w-full" data-id="element-289">
        <h1 className="text-[20px] font-medium text-[#e8e4df] tracking-tight mb-6" data-id="element-290">Browser</h1>

        <div className="relative mb-6" data-id="element-291">
          <Search size={14} className="absolute left-0 top-1/2 -translate-y-1/2 text-[#8a8680]/40" data-id="element-292" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search bookmarks" className="w-full bg-transparent text-[#e8e4df] pl-6 pr-4 py-2 text-[14px] outline-none placeholder:text-[#8a8680]/30 border-b border-[#8a8680]/10 focus:border-[#8a8680]/30 transition-colors" data-id="element-293" />
        </div>

        <div className="flex items-center gap-3 flex-wrap mb-8" data-id="element-294">
          {ALL_TAGS.slice(0, 10).map(tag => {
          const active = activeTags.includes(tag);
          return <button key={tag} onClick={() => toggleTag(tag)} className={`text-[12px] transition-colors ${active ? 'text-[#4a9a9a]' : 'text-[#8a8680]/50 hover:text-[#8a8680]'}`} data-id="element-295">{tag}</button>;
        })}
        </div>

        {pinned.length > 0 && <section className="mb-8" data-id="element-296">
            <div className="flex flex-col gap-3" data-id="element-297">
              {pinned.map((bm, i) => <BookmarkCard key={bm.id} bookmark={bm} index={i} onClick={() => setViewingId(bm.id)} data-id="element-298" />)}
            </div>
          </section>}

        {rest.length > 0 && <section data-id="element-299">
            <div className="flex flex-col gap-3" data-id="element-300">
              {rest.map((bm, i) => <BookmarkCard key={bm.id} bookmark={bm} index={i + pinned.length} onClick={() => setViewingId(bm.id)} data-id="element-301" />)}
            </div>
          </section>}

        {filtered.length === 0 && <EmptyState message="No bookmarks match." data-id="element-302" />}

        <div className="mt-8" data-id="element-303">
          <AnimatePresence data-id="element-304">
            {showAdd && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} className="overflow-hidden mb-4" data-id="element-305">
                <div className="bg-[#1c1c22] rounded-[16px] p-5 space-y-3" data-id="element-306">
                  <input type="text" value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="Title" className="w-full bg-transparent text-[#e8e4df] text-[14px] outline-none placeholder:text-[#8a8680]/30 border-b border-[#8a8680]/10 pb-2" data-id="element-307" />
                  <input type="url" value={newUrl} onChange={e => setNewUrl(e.target.value)} placeholder="https://" className="w-full bg-transparent text-[#e8e4df] text-[14px] outline-none placeholder:text-[#8a8680]/30 border-b border-[#8a8680]/10 pb-2 font-mono" data-id="element-308" />
                  <div className="flex gap-6 pt-2" data-id="element-309"><button onClick={() => {
                  setNewTitle('');
                  setNewUrl('');
                  setShowAdd(false);
                }} className="text-[13px] text-[#4a9a9a]/80 hover:text-[#4a9a9a]" data-id="element-310">Save</button><button onClick={() => setShowAdd(false)} className="text-[13px] text-[#8a8680]/50 hover:text-[#8a8680]" data-id="element-311">Cancel</button></div>
                </div>
              </motion.div>}
          </AnimatePresence>
          {!showAdd && <button onClick={() => setShowAdd(true)} className="flex items-center gap-2 text-[#8a8680]/50 hover:text-[#e8e4df]/80 transition-colors text-[13px]" data-id="element-312"><Plus size={13} data-id="element-313" />Add bookmark</button>}
        </div>
      </div>
    </div>;
}
function BookmarkCard({
  bookmark,
  index,
  onClick
}: {
  bookmark: Bookmark;
  index: number;
  onClick: () => void;
}) {
  return <motion.button initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    duration: 0.3,
    delay: index * 0.03
  }} onClick={onClick} className="bg-[#1c1c22] rounded-[16px] p-5 text-left group hover:bg-[#1e1e25] transition-colors w-full" data-id="element-314">
      <div className="flex items-center gap-4" data-id="element-315">
        <div className="w-10 h-10 rounded-[12px] bg-[#252530] flex items-center justify-center shrink-0" data-id="element-316">
          <Globe size={16} className="text-[#8a8680]/60" strokeWidth={1.5} data-id="element-317" />
        </div>
        <div className="flex-1 min-w-0" data-id="element-318">
          <div className="flex items-center gap-2" data-id="element-319">
            <span className="text-[15px] text-[#e8e4df] truncate" data-id="element-320">{bookmark.title}</span>
            {bookmark.authRequired && bookmark.authStatus === 'needs-login' && <Lock size={11} className="text-amber-400/50 shrink-0" data-id="element-321" />}
          </div>
          <p className="text-[12px] text-[#8a8680]/40 mt-0.5 truncate" data-id="element-322">{bookmark.domain}</p>
        </div>
        {bookmark.lastVisited && <span className="text-[12px] text-[#8a8680]/40 shrink-0" data-id="element-323">{bookmark.lastVisited}</span>}
      </div>
    </motion.button>;
}
function BrowserDetail({
  bookmark,
  onBack
}: {
  bookmark: Bookmark;
  onBack: () => void;
}) {
  const [mode, setMode] = useState<'info' | 'embedded'>('info');
  const [agentTask, setAgentTask] = useState<AgentTask | null>(null);
  const [taskInput, setTaskInput] = useState('');
  const [showTaskInput, setShowTaskInput] = useState(false);
  const [authDismissed, setAuthDismissed] = useState(false);
  const needsAuth = bookmark.authRequired && bookmark.authStatus === 'needs-login' && !authDismissed;
  const submitTask = () => {
    if (!taskInput.trim()) return;
    if (needsAuth) {
      setAgentTask({
        description: taskInput,
        status: 'auth-required'
      });
    } else {
      setAgentTask({
        description: taskInput,
        status: 'working'
      });
      setTimeout(() => setAgentTask(prev => prev ? {
        ...prev,
        status: 'done'
      } : null), 3000);
    }
    setTaskInput('');
    setShowTaskInput(false);
  };
  const handleAuth = () => {
    setAuthDismissed(true);
    if (agentTask && agentTask.status === 'auth-required') {
      setAgentTask({
        ...agentTask,
        status: 'working'
      });
      setTimeout(() => setAgentTask(prev => prev ? {
        ...prev,
        status: 'done'
      } : null), 3000);
    }
  };
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} transition={{
    duration: 0.3
  }} className="min-h-[calc(100vh-56px)] text-[#e8e4df] pt-12 pb-32 px-6" data-id="element-324">
      <div className="max-w-[640px] mx-auto w-full" data-id="element-325">
        <button onClick={onBack} className="flex items-center gap-2 text-[#8a8680]/60 hover:text-[#e8e4df] transition-colors -ml-1 mb-8" data-id="element-326"><ArrowLeft size={14} data-id="element-327" /><span className="text-[13px]" data-id="element-328">Browser</span></button>

        <header className="mb-6" data-id="element-329">
          <h1 className="text-[20px] font-medium text-[#e8e4df] tracking-tight" data-id="element-330">{bookmark.title}</h1>
          <p className="text-[13px] text-[#8a8680]/50 mt-1 font-mono" data-id="element-331">{bookmark.domain}</p>
        </header>

        {/* Mode toggle */}
        <div className="flex items-center gap-6 mb-6 border-b border-[#8a8680]/10" data-id="element-332">
          <button onClick={() => setMode('info')} className={`pb-3 text-[14px] transition-colors relative ${mode === 'info' ? 'text-[#e8e4df]' : 'text-[#8a8680] hover:text-[#e8e4df]/80'}`} data-id="element-333">Open{mode === 'info' && <motion.div layoutId="browserMode" className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#4a9a9a]" initial={false} transition={{
            type: 'spring',
            stiffness: 500,
            damping: 30
          }} data-id="element-334" />}</button>
          <button onClick={() => setMode('embedded')} className={`pb-3 text-[14px] transition-colors relative ${mode === 'embedded' ? 'text-[#e8e4df]' : 'text-[#8a8680] hover:text-[#e8e4df]/80'}`} data-id="element-335">Embedded{mode === 'embedded' && <motion.div layoutId="browserMode" className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#4a9a9a]" initial={false} transition={{
            type: 'spring',
            stiffness: 500,
            damping: 30
          }} data-id="element-336" />}</button>
        </div>

        {mode === 'info' && <div className="flex flex-col gap-4" data-id="element-337">
            <a href={bookmark.url} target="_blank" rel="noopener noreferrer" className="bg-[#1c1c22] rounded-[16px] p-5 flex items-center justify-between group hover:bg-[#1e1e25] transition-colors" data-id="element-338">
              <div data-id="element-339">
                <p className="text-[14px] text-[#e8e4df]/80" data-id="element-340">Open in new tab</p>
                <p className="text-[12px] text-[#8a8680]/40 mt-1 font-mono truncate" data-id="element-341">{bookmark.url}</p>
              </div>
              <ExternalLink size={16} className="text-[#8a8680]/40 group-hover:text-[#8a8680] transition-colors shrink-0" data-id="element-342" />
            </a>

            {bookmark.tags.length > 0 && <div className="flex items-center gap-3 flex-wrap" data-id="element-343">
                {bookmark.tags.map(tag => <span key={tag} className="text-[12px] text-[#8a8680]/50" data-id="element-344">{tag}</span>)}
              </div>}
          </div>}

        {mode === 'embedded' && <div data-id="element-345">
            {/* Auth prompt */}
            <AnimatePresence data-id="element-346">
              {needsAuth && !agentTask && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} className="bg-[#1c1c22] rounded-[16px] p-5 mb-4" data-id="element-347">
                  <div className="flex items-center gap-3 mb-3" data-id="element-348">
                    <Lock size={14} className="text-amber-400/60" data-id="element-349" />
                    <span className="text-[14px] text-[#e8e4df]/80" data-id="element-350">Authentication required</span>
                  </div>
                  <p className="text-[13px] text-[#8a8680]/60 leading-[1.5] mb-4" data-id="element-351">This site needs you to log in. Sign in on any device and the agent can continue.</p>
                  <button onClick={handleAuth} className="text-[13px] text-[#4a9a9a]/80 hover:text-[#4a9a9a] transition-colors" data-id="element-352">Sign in</button>
                </motion.div>}
            </AnimatePresence>

            {/* Neko browser mock */}
            <div className="bg-[#0a0a0e] rounded-[16px] overflow-hidden border border-[#8a8680]/10" data-id="element-353">
              <div className="flex items-center gap-2 px-4 py-2.5 bg-[#141418] border-b border-[#8a8680]/10" data-id="element-354">
                <div className="flex items-center gap-1.5" data-id="element-355">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#8a8680]/20" data-id="element-356" />
                  <div className="w-2.5 h-2.5 rounded-full bg-[#8a8680]/20" data-id="element-357" />
                  <div className="w-2.5 h-2.5 rounded-full bg-[#8a8680]/20" data-id="element-358" />
                </div>
                <div className="flex-1 bg-[#0a0a0e] rounded-md px-3 py-1 ml-2" data-id="element-359">
                  <span className="text-[11px] text-[#8a8680]/50 font-mono truncate" data-id="element-360">{bookmark.url}</span>
                </div>
                <Monitor size={12} className="text-[#8a8680]/30" data-id="element-361" />
              </div>
              <div className="aspect-[16/10] flex items-center justify-center relative" data-id="element-362">
                <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0e] to-[#141418]" data-id="element-363" />
                <div className="relative z-10 flex flex-col items-center gap-3" data-id="element-364">
                  <Globe size={24} className="text-[#8a8680]/20" data-id="element-365" />
                  <p className="text-[13px] text-[#8a8680]/40" data-id="element-366">{bookmark.domain}</p>
                  {needsAuth && !authDismissed && <p className="text-[11px] text-amber-400/40" data-id="element-367">waiting for authentication</p>}
                </div>
              </div>
            </div>

            {/* Agent task area */}
            <div className="mt-4" data-id="element-368">
              <AnimatePresence data-id="element-369">
                {agentTask && <motion.div initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} className="bg-[#1c1c22] rounded-[16px] p-5 mb-4" data-id="element-370">
                    <p className="text-[14px] text-[#e8e4df]/80 leading-[1.5]" data-id="element-371">{agentTask.description}</p>
                    <div className="mt-3" data-id="element-372">
                      {agentTask.status === 'auth-required' && <div className="flex items-center gap-3" data-id="element-373">
                          <Lock size={12} className="text-amber-400/60" data-id="element-374" />
                          <span className="text-[12px] text-amber-400/60" data-id="element-375">needs authentication to proceed</span>
                          <button onClick={handleAuth} className="text-[12px] text-[#4a9a9a]/80 hover:text-[#4a9a9a] ml-auto" data-id="element-376">Sign in</button>
                        </div>}
                      {agentTask.status === 'working' && <div className="flex items-center gap-2" data-id="element-377">
                          <div className="w-3 h-3 border border-[#4a9a9a]/30 border-t-[#4a9a9a] rounded-full animate-spin" data-id="element-378" />
                          <span className="text-[12px] text-[#4a9a9a]/60" data-id="element-379">working</span>
                        </div>}
                      {agentTask.status === 'done' && <div className="flex items-center gap-2" data-id="element-380">
                          <Check size={12} className="text-[#4a9a9a]/70" data-id="element-381" />
                          <span className="text-[12px] text-[#4a9a9a]/60" data-id="element-382">done</span>
                        </div>}
                    </div>
                  </motion.div>}
              </AnimatePresence>

              {!showTaskInput ? <button onClick={() => setShowTaskInput(true)} className="flex items-center gap-2 text-[#8a8680]/50 hover:text-[#e8e4df]/80 transition-colors text-[13px]" data-id="element-383">Ask agent to do something here</button> : <div className="flex items-center gap-3" data-id="element-384">
                  <input type="text" value={taskInput} onChange={e => setTaskInput(e.target.value)} onKeyDown={e => {
              if (e.key === 'Enter') submitTask();
              if (e.key === 'Escape') setShowTaskInput(false);
            }} placeholder="e.g. update row 12 with this month's figures" className="flex-1 bg-transparent text-[#e8e4df] text-[14px] outline-none border-b border-[#8a8680]/20 pb-1 placeholder:text-[#8a8680]/30" autoFocus data-id="element-385" />
                  <button onClick={submitTask} className="text-[13px] text-[#4a9a9a]/80 hover:text-[#4a9a9a]" data-id="element-386">Go</button>
                </div>}
            </div>
          </div>}
      </div>
    </motion.div>;
}